/**
 * F-CoalgebraExplainer — progressive-disclosure explainer for F-coalgebras,
 * terminal F-coalgebras, anamorphisms, and coalgebra homomorphisms.
 *
 * The dual of the F-algebra pilot. Where algebras absorb structure into a
 * value, coalgebras observe one layer of behavior out of a state. CSK uses
 * coalgebras for INV.PRESERVE: every transition exposes one observation
 * step that lands back in the invariant's domain.
 *
 * Acceptance criteria (issue #1551 — coalgebra side):
 *   - F-coalgebra pilot                       ✓ formal-tier square
 *   - Terminal F-coalgebra + anamorphism      ✓ formal-tier `TerminalCoalgebraDiagram`
 *   - "Behavioral equivalence = canonical
 *      bisimulation" cross-link               ✓ inline + cross-links
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  CalloutBox,
  CoalgebraHomomorphismSquare,
  ExplainerCard,
  FCoalgebraDiagram,
  TerminalCoalgebraDiagram,
  WorkedExample,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = THEOREM_COLOR["INV.PRESERVE"];
const ALGEBRA_ACCENT = THEOREM_COLOR["INV.ESTABLISH"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-fcoalgebra",
  kind: "Categorical structure",
  title: "F-Coalgebra",
  subtitle: "(S, γ : S → F(S)) · νF · ana γ · Lambek dual",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "INV.PRESERVE (I2)", href: "#explainer-inv-preserve", accent: ACCENT },
  { label: "Bisimulation", href: "#explainer-bisimulation", accent: ACCENT },
  { label: "Recursion schemes", href: "#explainer-recursion-schemes" },
  { label: "F-Algebra", href: "#explainer-falgebra", accent: ALGEBRA_ACCENT },
  { label: "Bialgebra", href: "#explainer-bialgebra" },
  { label: "Adjunction", href: "#explainer-adjunction" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      An <strong>F-coalgebra</strong> is the dual of an F-algebra: instead of absorbing structure
      into a value, it reveals one layer of behavior out of a state. CSK uses it to make "every step
      preserves the invariant" mean exactly that, with no escape hatch.
    </p>
  ),
  intuition: (
    <>
      <p>
        Pick a streaming server. From any state <MathInline>{tex`s`}</MathInline> you can read off
        "the next packet (or end-of-stream), and the rest of the stream." That shape is the functor{" "}
        <MathInline>{tex`F(X) = 1 + (\mathrm{Packet} \times X)`}</MathInline>. An{" "}
        <strong>F-coalgebra</strong> <MathInline>{tex`\gamma : S \to F(S)`}</MathInline> is the rule
        that says, for any current state, what the next observation looks like and what state
        remains.
      </p>
      <p>
        Where an F-algebra answers "given the parts, what is the whole?", an F-coalgebra answers
        "given the whole, what are the next parts?" Streams, automata, parser cursors, process
        traces, and lazy values are all coalgebras for an appropriate{" "}
        <MathInline>{tex`F`}</MathInline>.
      </p>
      <p>
        The canonical (<strong>terminal</strong>) coalgebra <MathInline>{tex`\nu F`}</MathInline> is
        the universe of every possible <MathInline>{tex`F`}</MathInline>-shaped behavior. Every
        coalgebra factors uniquely through <MathInline>{tex`\nu F`}</MathInline> by the{" "}
        <em>anamorphism</em> <MathInline>{tex`\mathrm{ana}\ \gamma : S \to \nu F`}</MathInline> —
        and two states that map to the same point in <MathInline>{tex`\nu F`}</MathInline> are
        exactly the states an outside observer cannot tell apart. That is the seed of{" "}
        <em>bisimulation</em>.
      </p>
      <CalloutBox eyebrow="The one-line takeaway" accent={ACCENT}>
        <em>F-coalgebras make "observable behavior" a first-class object.</em> Anything you can
        actually see about a system lives in <MathInline>{tex`\nu F`}</MathInline>; anything not
        visible by some coalgebra step is, by construction, invisible to an attacker.
      </CalloutBox>
    </>
  ),
  formal: (
    <>
      <p>
        For an endofunctor <MathInline>{tex`F : C \to C`}</MathInline>, an{" "}
        <strong>F-coalgebra</strong> is a pair <MathInline>{tex`(S, \gamma)`}</MathInline> with{" "}
        <MathInline>{tex`\gamma : S \to F(S)`}</MathInline>. Coalgebra morphisms{" "}
        <MathInline>{tex`h : (S, \gamma) \to (T, \delta)`}</MathInline> commute the dual square{" "}
        <MathInline>{tex`F(h) \circ \gamma = \delta \circ h`}</MathInline> — the coalgebra version
        says "observe first, then transport" instead of the algebra's "transport first, then
        collapse":
      </p>
      <CoalgebraHomomorphismSquare accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>
        Compare with the algebra square{" "}
        <MathInline>{tex`h \circ \alpha = \beta \circ F(h)`}</MathInline>: the directions of{" "}
        <MathInline>{tex`h`}</MathInline> and <MathInline>{tex`F(h)`}</MathInline> are swapped. A
        coalgebra morphism says that observing the source state and then transporting is the same as
        transporting and then observing — the observation discipline is preserved. By contrast, the
        algebra square says "apply α first, then h" equals "apply F(h) first, then β": transport
        first, then evaluate.
      </p>
      <p style={{ marginTop: "0.75rem" }}>
        When the <strong>terminal F-coalgebra</strong> <MathInline>{tex`\nu F`}</MathInline> exists,
        two facts mirror Lambek's lemma and the catamorphism:
      </p>
      <TerminalCoalgebraDiagram accent={ACCENT} />
      <ul style={{ marginTop: "0.5rem" }}>
        <li>
          <strong>Lambek (dual).</strong>{" "}
          <MathInline>{tex`\mathrm{out} : \nu F \to F(\nu F)`}</MathInline> on the terminal
          coalgebra is an isomorphism, so <MathInline>{tex`\nu F \cong F(\nu F)`}</MathInline>. The
          terminal carrier <em>is</em> its own observation horizon.
        </li>
        <li>
          <strong>Anamorphism.</strong> For any coalgebra{" "}
          <MathInline>{tex`(S, \gamma)`}</MathInline>, the unique coalgebra morphism{" "}
          <MathInline>{tex`\mathrm{ana}\ \gamma : S \to \nu F`}</MathInline> exists.{" "}
          <MathInline>{tex`\mathrm{ana}\ \gamma`}</MathInline> sends each state to its full infinite
          observation trace.
        </li>
        <li>
          <strong>Behavioral equivalence.</strong> Two states are <em>bisimilar</em> iff they agree
          in <MathInline>{tex`\nu F`}</MathInline>:{" "}
          <MathInline>{tex`s_{1} \sim s_{2} \;\Leftrightarrow\; \mathrm{ana}\ \gamma\ (s_{1}) = \mathrm{ana}\ \gamma\ (s_{2})`}</MathInline>
          . The largest bisimulation is the kernel of the anamorphism — that is the "canonical
          bisimulation" theorem. This is the coinductive principle: to prove bisimilarity, exhibit a
          bisimulation relation; the proof goes "into the future" rather than "into the past."
        </li>
      </ul>
    </>
  ),
  relevance: (
    <>
      <p>
        CSK pairs each invariant predicate with an F-coalgebra on the state.{" "}
        <strong>INV.PRESERVE</strong> is the obligation that <MathInline>{tex`\gamma`}</MathInline>{" "}
        lands in <code>F(Valid)</code> rather than in arbitrary <code>F(S)</code>: every observation
        the system can make of a valid state is itself a valid F-shape. Stepping cannot push the
        system out of the invariant.
      </p>
      <WorkedExample
        eyebrow="Real-world story"
        title="A rate limiter that cannot leak its budget"
        accent={ACCENT}
        scenario={
          <>
            A token-bucket rate limiter has state <MathInline>{tex`S = \mathbb{N}`}</MathInline>{" "}
            (remaining tokens). One observation is{" "}
            <MathInline>{tex`F(X) = \mathrm{Allow} \times X + \mathrm{Deny}`}</MathInline>: either
            "we allowed the request and here is the next state" or "we denied." The coalgebra{" "}
            <MathInline>{tex`\gamma : \mathbb{N} \to F(\mathbb{N})`}</MathInline> is the limiter's
            stepping rule. The invariant is{" "}
            <MathInline>{tex`\mathrm{Valid} = \mathrm{tokens} \le \mathrm{capacity}`}</MathInline>.
          </>
        }
        withoutStructure={
          <>
            A state machine permits a sneaky implementation: store the bucket externally, allow
            requests against a phantom counter, then desynchronize. The transition function is
            opaque — an auditor only sees inputs and outputs.
          </>
        }
        withStructure={
          <>
            Typing <MathInline>{tex`\gamma : \mathrm{Valid} \to F(\mathrm{Valid})`}</MathInline>{" "}
            rather than <MathInline>{tex`\gamma : \mathbb{N} \to F(\mathbb{N})`}</MathInline> forces
            every step to land back inside the invariant. Two limiters that share the same coalgebra
            are bisimilar — they produce identical traces by construction. Side-channel leakage of
            the bucket is now <em>structurally inexpressible</em>.
          </>
        }
      />
      <p style={{ marginTop: "0.75rem" }}>
        Two states map to the same point in <MathInline>{tex`\nu F`}</MathInline> iff they are{" "}
        <strong>bisimilar</strong>. CSK uses this single categorical statement for noninterference,
        opacity, and refactoring soundness: if two implementations are bisimilar, they neutralize
        the same attack classes — by construction, not by inspection. See the <em>Bisimulation</em>{" "}
        explainer for the interactive walk-through.
      </p>
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras (coalgebra & bisimulation, §3 + §9)",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "F-coalgebra, terminal coalgebra, and the canonical bisimulation theorem.",
    },
    {
      label: "Final coalgebra (Agda)",
      path: "agda/Kernel/INV/PRESERVE/FinalCoalgebra.agda",
      note: "Terminal F-coalgebra used by the INV.PRESERVE neutralization.",
    },
    {
      label: "INV.PRESERVE neutralization (Agda)",
      path: "agda/Kernel/INV/PRESERVE/Neutralization.agda",
      note: "Mechanized proof that γ : Valid → F(Valid) makes the unsafe step inexpressible.",
    },
    {
      label: "Recursion schemes (coalgebra side, Agda)",
      path: "agda/MathLibCore/Lemmas/RecursionSchemes/Coalgebras.agda",
      note: "ana and apo as the canonical unfolds into νF.",
    },
    {
      label: "Structural Invariant Safety Guarantees manuscript (PR #1544)",
      path: "https://github.com/InvariantHoldings/categorical-safety-kernel/pull/1544",
      note: "Chapter 03 (F-Coalgebras / Invariant Preservation) and Chapter 04 (joint DPG).",
    },
  ],
};

export function FCoalgebraExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<FCoalgebraDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
